import React, { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import {
    primeSupervisionStatus,
    useSupervisionStatus,
} from '../features/parentPortal/supervisionStatus';

/**
 * Resolves the default landing page for a freshly-authenticated account from its
 * state — not from a sign-in toggle. This is where post-login navigation points.
 *
 *   - active subscription        -> /dashboard (learner experience)
 *   - no subscription, supervises -> /family (free parent portal)
 *   - no subscription, neither    -> /subscribe (paywall; a brand-new supervisor
 *                                    reaches /family from there via "continue free")
 *
 * This runs ONLY as the default landing. Individual gated routes don't make this
 * choice — RequireSubscription just sends unsubscribed users to /subscribe — so a
 * supervisor is never trapped away from a page they navigate to directly.
 */
export default function HomeRedirect() {
    const { user, isSubscribed, subscriptionLoading, ready } = useAuth();
    const { supervises, ready: supervisionReady } = useSupervisionStatus();

    useEffect(() => {
        primeSupervisionStatus(user?.uid || null);
    }, [user?.uid]);

    // Wait until both answers are known so we resolve once, without a flicker
    // through the wrong page. Rendered without the app shell (see App.js), so
    // this is a self-contained full-screen loader.
    if (!ready || subscriptionLoading || !supervisionReady) {
        return (
            <div
                role="status"
                aria-label="Loading your workspace"
                style={{
                    position: 'fixed',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--bg-primary)',
                }}
            >
                <svg
                    width="32"
                    height="32"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--accent-primary)"
                    strokeWidth="2"
                    style={{ animation: 'graspd-spin 1s linear infinite' }}
                >
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
            </div>
        );
    }

    if (isSubscribed) return <Navigate to="/dashboard" replace />;
    if (supervises) return <Navigate to="/family" replace />;
    return <Navigate to="/subscribe" replace />;
}
